import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ServiceCard } from '../components/ServiceCard';
import { MapPin, Clock, Star, Mic, Phone, Award, Users, Shield, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BlackFridayBadge } from '../components/BlackFridayBadge';
import { SEOHead, createLocalBusinessSchema } from '../components/SEOHead';
import { EnhancedSEO } from '../components/EnhancedSEO';
import { StaticFallback } from '../components/StaticFallback';
import { FacebookFeed } from '../components/FacebookFeed';
import { ReviewSubmission } from '../components/ReviewSubmission';
import { PublicReviews } from '../components/PublicReviews';
import { HolidayDiscountBanner } from '../components/HolidayDiscountBanner';
import { allFAQSchemas } from '../utils/faqSchemas';
import { websiteSchema, generateBreadcrumb } from '../utils/structuredData';
import { eastendOrganizationSchema } from '../utils/businessSchemas';
import { tanningSalonSchema, laundryBusinessSchema, foodEstablishmentSchema } from '../utils/seoSchemas';
import { Link } from 'react-router-dom';

export default function Home() {
  const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: 'easeOut' } };
  const [blogPosts, setBlogPosts] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Fetch latest 3 blog posts
    const fetchBlogPosts = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/blog/posts`);
        if (res.ok) {
          const data = await res.json();
          setBlogPosts(data.slice(0, 3)); // Get latest 3
        }
      } catch (e) {
        console.log('Could not fetch blog posts');
      }
    };
    fetchBlogPosts();
  }, [backendUrl]);

  const breadcrumbs = generateBreadcrumb([
    { name: 'Home', path: '/' }
  ]);

  return (
    <div className="min-h-screen">
      {/* Static Fallback HTML for Crawlers */}
      <StaticFallback page="home" />
      
      {/* Enhanced SEO with FAQs and Structured Data */}
      <EnhancedSEO
        title="#1 Tanning & Laundry in Mt Vernon, Ohio | Eastend Tanning & Laundry"
        description="Eastend Tanning & Laundry - Mt Vernon's premier tanning salon & coin laundry since 1998. 6 tanning bed levels, Matrix Bed, red light therapy, free drying daily. 818 Coshocton Ave. (740) 397-9632. Black Friday BOGO deals!"
        keywords="best tanning salon Mt Vernon Ohio, coin laundry Mt Vernon, Eastend Tanning, laundromat Knox County, tanning beds Mt Vernon, wash dry fold, Matrix tanning bed, red light therapy Mt Vernon, 818 Coshocton Ave, trusted local business"
        canonicalUrl="https://eastend.website/"
        faqSchema={allFAQSchemas.home}
        structuredData={[eastendOrganizationSchema, websiteSchema, tanningSalonSchema, laundryBusinessSchema, foodEstablishmentSchema, createLocalBusinessSchema()]}
        breadcrumbs={breadcrumbs}
        ogImage="https://eastend.website/images/eastend-hero.jpg"
      />
      
      {/* Dynamic Holiday/Seasonal Discount Banner */}
      <HolidayDiscountBanner variant="compact" />
      
      {/* Hero Section */}
      <section data-testid="home-hero" className="relative py-12 sm:py-16 lg:py-20 bg-[linear-gradient(135deg,hsl(43_96%_96%),hsl(183_45%_96%))] overflow-hidden">
        <div className="noise"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px] relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-12">
            {/* Black Friday Banner */}
            <div className="mb-6">
              <BlackFridayBadge className="text-2xl px-8 py-4 inline-block shadow-2xl" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-[-0.01em] text-foreground mb-4">#1 Tanning & Laundry in Mt Vernon, Ohio</h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">Eastend Tanning & Laundry offers premium tanning beds, red light therapy, and professional laundry with free drying every day at 818 Coshocton Ave, serving Mt Vernon and Knox County, Ohio.</p>
            {/* Hero CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button data-testid="home-hero-primary-cta-button" size="lg" onClick={() => window.openMaryChatAndListen && window.openMaryChatAndListen()} className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-6 h-12 font-semibold shadow-md hover:bg-[hsl(42_92%_50%)] hover:shadow-lg transition-all duration-200">
                <Mic className="w-4 h-4" />
                Talk to Mary
              </Button>
              <a href="#locations" data-testid="home-hero-secondary-cta-button">
                <Button variant="outline" size="lg" className="inline-flex items-center gap-2 px-6 h-12 font-semibold border-2 hover:bg-white/50 transition-all duration-200">
                  <MapPin className="w-4 h-4" />
                  Find a Location
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Bento Service Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <ServiceCard title="Tanning Studio" description="5 tanning levels including Matrix, stand-up, and red-light therapy. Achieve your perfect glow." ctaText="Book Tanning" href="/tanning" imageUrl="https://customer-assets.emergentagent.com/job_tanning-chatbot/artifacts/zne70emi_Screenshot_20230527-083315_Gallery.jpg" tone="tanning" altText="Professional tanning bed with red light therapy at Eastend Tanning, Mt Vernon, OH" />
            <ServiceCard title="Laundromat" description="Two convenient locations with modern washers and dryers. Clean, safe, and always open." ctaText="Laundry Drop-Off Service" href="/laundry" imageUrl="https://customer-assets.emergentagent.com/job_tanning-chatbot/artifacts/iq5hip31_Screenshot_20251108_054922_Google.jpg" tone="laundry" altText="Modern coin-operated washing machines at Eastend Laundromat, Mt Vernon, OH" />
            <ServiceCard title="Fizze Drinks" description="Bubble tea, dirty sodas, energy bombs, and more. Refreshing flavors to brighten your day." ctaText="Order Fizze Drinks" href="/drinks" imageUrl="https://images.pexels.com/photos/9090072/pexels-photo-9090072.jpeg" tone="drinks" altText="Fresh bubble tea and milk tea drinks at Fizze, Eastend Tanning Mt Vernon" />
            <ServiceCard title="Fast Nails" description="Professional nail care with manicures, pedicures, gel services, and nail art. Call (740) 485-8158 for appointments and enquiries." ctaText="Call (740) 485-8158 to Book" href="/nails" imageUrl="https://images.unsplash.com/photo-1571290274554-6a2eaa771e5f?q=80&w=1200&auto=format&fit=crop" tone="drinks" altText="Professional nail services, manicures and pedicures at Fast Nails, Eastend Mt Vernon" />
            <ServiceCard 
              title="818 Food Truck Stop" 
              description="Order authentic African cuisine or book your food truck spot! Prime location opposite Kroger. $70/day with electricity and water included." 
              ctaText="View Menu & Order" 
              href="/eats" 
              secondaryCta="Book Food Truck Spot"
              secondaryHref="/foodtruck"
              imageUrl="https://customer-assets.emergentagent.com/job_76fcc203-fcee-43f8-b1e6-f53b7d9ae0f3/artifacts/9q8enxcg_Screenshot_20260111_172022_Facebook.jpg" 
              imageClassName="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
              tone="foodtruck" 
              altText="Succulent Ghana Jollof Rice - 818 Food Truck Stop at 818 Coshocton Ave, Mt Vernon" 
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section data-testid="about-section" className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6 text-foreground">Your Local Mount Vernon Destination</h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4">Eastend Tanning & Laundry is proudly locally owned and operated, serving the Mount Vernon community with premium tanning services, spotless laundromats, and refreshing Fizze drinks. We're committed to providing a welcoming, clean, and convenient experience for all our customers.</p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">Whether you're looking for the perfect tan, need to tackle laundry day, or want to grab a delicious drink, we've got you covered. Visit us today and discover why we're Mount Vernon's favorite spot.</p>
          </div>
        </div>
      </section>

      {/* Locations Strip */}
      <section id="locations" data-testid="locations-section" className="py-12 lg:py-20 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center text-foreground">Visit Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Eastend Location */}
            <Card data-testid="location-card-eastend" className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">Eastend Tanning & Laundry</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><span>818 Coshocton Ave, Mt Vernon, OH 43050</span></div>
                <div className="flex items-start gap-2 text-muted-foreground"><Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><div><p data-testid="hours-eastend" className="font-medium">Mon-Sun: 8:00 AM - 7:30 PM</p></div></div>
              </div>
              <div className="mt-5 flex gap-3">
                <Button data-testid="location-talk-mary-eastend" onClick={() => window.openMaryChatAndListen && window.openMaryChatAndListen()} size="sm" className="bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(183_55%_38%)]">Talk to Mary</Button>
                <a data-testid="location-directions-button-eastend" href="https://www.google.com/maps/dir/?api=1&destination=818+Coshocton+Ave,+Mt+Vernon,+OH+43050" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm">Directions</Button></a>
              </div>
            </Card>

            {/* Fizze Drinks */}
            <Card data-testid="location-card-fizze" className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">Fizze Drinks</h3>
              <Badge variant="secondary" className="mb-3">Bubble Tea Shop</Badge>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><span>818 Coshocton Ave, Mt Vernon, OH 43050</span></div>
                <div className="flex items-start gap-2 text-muted-foreground"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><a href="tel:+17403979632" className="hover:underline">(740) 397-9632</a></div>
                <div className="flex items-start gap-2 text-muted-foreground"><Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><div><p data-testid="hours-fizze" className="font-medium">Open Daily: 8:00 AM - 6:00 PM</p></div></div>
              </div>
              <div className="mt-5 flex gap-3">
                <a href="/drinks"><Button data-testid="location-view-menu-fizze" size="sm" className="bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(183_55%_38%)]">View Menu</Button></a>
                <a data-testid="location-directions-button-fizze" href="https://www.google.com/maps/dir/?api=1&destination=818+Coshocton+Ave,+Mt+Vernon,+OH+43050" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm">Directions</Button></a>
              </div>
            </Card>

            {/* Westend Location */}
            <Card data-testid="location-card-westend" className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">Westend Laundry</h3>
              <Badge variant="secondary" className="mb-3">Coin Laundry Only</Badge>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><span>116 S Norton St, Mt Vernon, OH 43050</span></div>
                <div className="flex items-start gap-2 text-muted-foreground"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><a href="tel:+17403979632" className="hover:underline">(740) 397-9632</a></div>
                <div className="flex items-start gap-2 text-muted-foreground"><Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" /><div><p data-testid="hours-westend" className="font-medium">Open Daily: 6:00 AM - 10:00 PM</p></div></div>
              </div>
              <div className="mt-5 flex gap-3">
                <Button data-testid="location-talk-mary-westend" onClick={() => window.openMaryChatAndListen && window.openMaryChatAndListen()} size="sm" className="bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(183_55%_38%)]">Talk to Mary</Button>
                <a data-testid="location-directions-button-westend" href="https://www.google.com/maps/dir/?api=1&destination=116+S+Norton+St,+Mt+Vernon,+OH+43050" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm">Directions</Button></a>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Facebook Feed Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center text-foreground">Latest from Eastend</h2>
          <div className="flex justify-center">
            <FacebookFeed 
              pageUrl="https://www.facebook.com/share/1CtZugxSec/" 
              pageName="Eastend Tanning & Laundry"
            />
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section data-testid="reviews-section" className="py-12 lg:py-20 bg-gradient-to-br from-amber-50 to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center text-foreground">What Our Customers Say</h2>
          
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Public Reviews Display */}
            <div>
              <h3 className="font-bold text-xl mb-6">Recent 5-Star Reviews</h3>
              <PublicReviews businessLocation="eastend" limit={5} />
            </div>
            
            {/* Review Submission Form */}
            <div>
              <ReviewSubmission defaultLocation="eastend" />
            </div>
          </div>

          <div className="text-center pt-8 border-t">
            <p className="text-base sm:text-lg text-muted-foreground mb-6">Love us? Leave a Google review too!</p>
            <a data-testid="google-reviews-link" href="https://www.google.com/maps/place/Eastend+Tanning+and+Laundry/@40.3930,-82.4850,17z" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="lg" className="inline-flex items-center gap-2 px-6 h-12 font-semibold hover:bg-muted transition-colors duration-200"><Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />Leave Google Review</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Food Truck CTA Section */}
      <section data-testid="foodtruck-cta-section" className="py-12 lg:py-20 bg-gradient-to-br from-[hsl(24_100%_96%)] to-[hsl(24_100%_98%)] border-y">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(24_100%_50%)] text-white mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
                  <path d="M15 18H9"/>
                  <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
                  <circle cx="17" cy="18" r="2"/>
                  <circle cx="7" cy="18" r="2"/>
                </svg>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4 text-foreground">Own a Food Truck?</h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed">Reserve your spot at 818 Food Truck Stop - the prime location opposite Kroger in Mt Vernon! Just $70/day with electricity and water included.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="/foodtruck" data-testid="foodtruck-cta-primary">
                  <Button size="lg" className="inline-flex items-center gap-2 bg-[hsl(24_100%_50%)] text-white hover:bg-[hsl(24_100%_45%)] px-8 h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
                      <path d="M15 18H9"/>
                      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
                      <circle cx="17" cy="18" r="2"/>
                      <circle cx="7" cy="18" r="2"/>
                    </svg>
                    Book Your Spot Now
                  </Button>
                </a>
                <a href="/foodtruck" data-testid="foodtruck-cta-secondary" className="text-[hsl(24_100%_40%)] font-semibold hover:underline inline-flex items-center gap-2">
                  Learn More
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Trust Signals & Authority Section */}
      <section className="py-12 bg-gradient-to-br from-amber-50 via-white to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-4">Trusted by Mt Vernon Since 1998</h2>
            <p className="text-lg text-muted-foreground">Family-owned local business serving Knox County for over 25 years</p>
          </div>

          {/* Trust Badges */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 text-center">
              <Award className="w-12 h-12 mx-auto mb-4 text-amber-600" />
              <h3 className="font-bold text-lg mb-2">Locally Owned Since 1998</h3>
              <p className="text-sm text-muted-foreground">Over 25 years serving Mt Vernon families with trusted tanning & laundry services</p>
            </Card>

            <Card className="p-6 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-teal-600" />
              <h3 className="font-bold text-lg mb-2">10,000+ Happy Customers</h3>
              <p className="text-sm text-muted-foreground">Trusted by Mt Vernon residents and OSU Knox Campus students</p>
            </Card>

            <Card className="p-6 text-center">
              <Shield className="w-12 h-12 mx-auto mb-4 text-orange-600" />
              <h3 className="font-bold text-lg mb-2">Clean & Safe Facility</h3>
              <p className="text-sm text-muted-foreground">Sanitized daily with well-maintained equipment and secure parking</p>
            </Card>
          </div>

          {/* Why Choose Us */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
            <h2 className="font-serif text-2xl font-bold text-center mb-6">Why Choose Eastend?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg mb-2">✓ Fast Tanning Results</h3>
                <p className="text-muted-foreground">High-powered beds including 40,740W Matrix bed for maximum efficiency</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">✓ Affordable Pricing</h3>
                <p className="text-muted-foreground">Monthly unlimited from $60, free drying every day at laundromat</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">✓ Clean Facility</h3>
                <p className="text-muted-foreground">Sanitized daily, well-maintained equipment, comfortable environment</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">✓ Family Owned</h3>
                <p className="text-muted-foreground">Local business that cares about our Mt Vernon community</p>
              </div>
            </div>
          </div>

          {/* Location & Map */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="font-serif text-2xl font-bold text-center mb-6">Visit Us in Mt Vernon</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-lg mb-4">📍 Our Location</h3>
                <p className="text-lg mb-2"><strong>818 Coshocton Ave</strong></p>
                <p className="text-lg mb-4">Mt Vernon, OH 43050</p>
                <p className="mb-2"><strong>Phone:</strong> (740) 397-9632</p>
                <p className="mb-4"><strong>Hours:</strong> Mon-Sun 8:00 AM - 7:30 PM</p>
                
                <div className="bg-teal-50 border-l-4 border-teal-600 p-4 mb-4">
                  <p className="font-semibold">🚗 Easy to Find</p>
                  <p className="text-sm">2 minutes from OSU Knox Campus on Coshocton Avenue, near Walmart</p>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-600 p-4">
                  <p className="font-semibold">🅿️ Ample Parking</p>
                  <p className="text-sm">Large parking lot behind building + front-door parking. Wheelchair accessible entrance.</p>
                </div>

                <div className="mt-6 space-y-2">
                  <p className="font-semibold">Connect With Us:</p>
                  <a href="https://www.facebook.com/share/1CtZugxSec/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline block">📘 Facebook - Eastend Tanning and Laundry</a>
                  <a href="https://www.instagram.com/eastendtanning?igsh=aXBvbzJtaGIyM3dx" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline block">📸 Instagram - Eastend Tanning</a>
                  <a href="https://www.yelp.com/biz/eastend-laundry-tanning-and-nutrition-mount-vernon" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline block">⭐ Yelp Reviews</a>
                  <a href="https://www.google.com/maps/place/818+Coshocton+Ave,+Mt+Vernon,+OH+43050" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline block">🗺️ Google Maps</a>
                </div>
              </div>

              <div>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.8267839507846!2d-82.48791472346436!3d40.39340997148084!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8836c0c0c7e05555%3A0x5555555555555555!2s818%20Coshocton%20Ave%2C%20Mt%20Vernon%2C%20OH%2043050!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%" 
                  height="400" 
                  style={{border: 0, borderRadius: '8px'}} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Eastend Tanning & Laundry Location Map"
                ></iframe>
                <p className="text-sm text-center text-muted-foreground mt-2">Click map for driving directions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Optimized for Voice & AI Search */}
      <section className="py-12 lg:py-20 bg-gradient-to-b from-muted to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[900px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center text-foreground">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">Where is Eastend Tanning & Laundry located?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  We're located at <strong>818 Coshocton Ave, Mt Vernon, OH 43050</strong>. We serve Mt Vernon and all of Knox County, Ohio. Our facility offers tanning, laundry, Fizze drinks, and nail services all under one roof.
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">What time does the laundromat close?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  <strong>Eastend (818 Coshocton Ave)</strong>: Open Mon-Sun 8:00 AM - 7:30 PM with drop-off service and attended washers. <strong>Westend (116 S Norton St)</strong>: Self-service coin laundry open daily 6:00 AM - 10:00 PM.
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">Do you offer free drying?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  Yes! At our Eastend location, we offer <strong>45 minutes of free drying every single day</strong>. After that, additional drying is just $0.25 for 7 minutes. This is one of the best values in Knox County!
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">How long are tanning sessions?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  Tanning session lengths vary by bed level: Level 1-2 beds typically 12-15 minutes, Level 3-4 beds 8-12 minutes, and our Matrix bed 6-10 minutes. Our staff will help you determine the right time for your skin type. Monthly unlimited packages let you tan as often as needed!
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">Do you take credit cards or cash?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  <strong>Eastend location</strong> accepts both cash and credit/debit cards for all services (no coins needed). <strong>Westend location</strong> is coin-operated only, but we have coin changer machines on-site for your convenience.
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">What tanning packages do you offer in Mt Vernon?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  We offer VIP unlimited tanning starting at $21.99/month (3-month commitment), Monthly unlimited with no commitment ($45.99-$194.99/month), 10-session packages, and single sessions. We have 5 tanning levels plus our exclusive 40,700-watt Matrix bed!
                </p>
              </div>
            </Card>

            <Card className="p-6" itemScope itemType="https://schema.org/Question">
              <h3 className="font-semibold text-lg mb-2" itemProp="name">Do you have parking and wheelchair access?</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="text-muted-foreground" itemProp="text">
                  Yes! Both locations offer free parking and are wheelchair accessible. We're committed to serving all members of the Mt Vernon and Knox County community.
                </p>
              </div>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">Have more questions? Talk to Mary, our AI assistant!</p>
            <Button onClick={() => window.openMaryChatAndListen && window.openMaryChatAndListen()} size="lg" className="bg-[hsl(var(--primary))] hover:bg-[hsl(42_92%_50%)]">
              Ask Mary Anything
            </Button>
          </div>
        </div>
      </section>


      {/* Latest Stories from Blog */}
      {blogPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-orange-600 text-white text-lg px-6 py-2">
                <BookOpen className="w-5 h-5 inline mr-2" />
                Latest Stories
              </Badge>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">People of Eastend Blog</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stories from our Mt Vernon community, tanning tips, and local highlights
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {blogPosts.map((post) => (
                <Card key={post.id || post._id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-bold text-xl mb-3 line-clamp-2">{post.title}</h3>
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {post.excerpt || post.content?.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {new Date(post.created_at || post.date).toLocaleDateString()}
                      </span>
                      <Link to={`/blog/${post.slug}`}>
                        <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
                          Read More <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/blog">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold">
                  View All Stories
                </Button>
              </Link>
            </div>

            {/* SEO Fallback for Blog Posts */}
            <noscript>
              <div className="mt-8 space-y-4">
                <h3 className="font-bold text-xl">Recent Blog Posts:</h3>
                {blogPosts.map((post) => (
                  <div key={post.id || post._id}>
                    <h4><a href={`/blog/${post.slug}`}>{post.title}</a></h4>
                    <p>{post.excerpt || post.content?.substring(0, 150)}</p>
                    <p className="text-sm">Published: {new Date(post.created_at || post.date).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </noscript>
          </div>
        </section>
      )}

    </div>
  );
}
